import React, { useContext, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  PerspectiveCamera,
  Points,
  PointMaterial,
  GizmoHelper,
  GizmoViewport,
} from "@react-three/drei";
import * as THREE from "three";
import { groupKeysList, Point3D } from "../cosmo-graph/model";
import { GraphContext } from "../cosmo-graph/data";

interface CloudCanvasProps {
  data: Point3D[];
  paused: boolean;
  onPointDoubleClick: (point: Point3D, points: Point3D[]) => void;
  onHover: (point: Point3D | null, x: number, y: number) => void;
  showUnknown: boolean;
  searchText: string;
  searchGroup: string;
}

const PointCloud: React.FC<{
  data: Point3D[];
  onPointDoubleClick: (point: Point3D, points: Point3D[]) => void;
  onHover: (point: Point3D | null, x: number, y: number) => void;
  showUnknown: boolean;
  searchText: string;
  searchGroup: string;
}> = ({
  data,
  onPointDoubleClick,
  onHover,
  showUnknown,
  searchText,
  searchGroup,
}) => {
  const pointsRef = useRef<THREE.Points>(null);
  const highlightedMaterialRef = useRef<THREE.PointsMaterial>(null);
  const { sharedData, groupData } = useContext(GraphContext);

  useFrame(({ clock }) => {
    if (highlightedMaterialRef.current) {
      const time = clock.getElapsedTime();
      // Pulse size between 1.0 and 2.5
      highlightedMaterialRef.current.size = 1.5 + Math.sin(time * 8);
    }
  });

  // Generate a circular texture
  const circleTexture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(16, 16, 15, 0, Math.PI * 2);
      ctx.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    return texture;
  }, []);

  const geometryData = useMemo(() => {
    if (data.length === 0)
      return {
        regular: {
          positions: new Float32Array(0),
          colors: new Float32Array(0),
        },
        highlighted: {
          positions: new Float32Array(0),
          colors: new Float32Array(0),
        },
      };

    const regularPositions: number[] = [];
    const regularColors: number[] = [];
    const regularPoints: Point3D[] = [];
    const highlightedPositions: number[] = [];
    const highlightedColors: number[] = [];
    const highlightedPoints: Point3D[] = [];

    // Background color for dimmed points (Slate 800)
    // const dimColor = new THREE.Color('#1e293b');
    const colorScratch = new THREE.Color();

    const restList = groupKeysList.filter(
      (key) => key !== sharedData.leftGroupKey
    );
    let rightGroupKey = sharedData.rightGroupKey || restList[0];
    if (!restList.includes(rightGroupKey)) {
      rightGroupKey = restList[0];
    }

    data.forEach((p) => {
      if (
        !showUnknown &&
        sharedData.leftGroupKey &&
        p[`:${sharedData.leftGroupKey}Name`] === "Unknown"
      ) {
        return;
      }
      if (searchText && searchGroup) {
        if (searchGroup === "all") {
          let isMatch = false;
          groupKeysList.forEach((key) => {
            if (
              (p[`:${key}Name`] as string).toLowerCase().indexOf(searchText) >=
              0
            ) {
              isMatch = true;
            }
          });
          if (!isMatch) {
            return;
          }
        } else {
          if (
            (p[`:${searchGroup}Name`] as string)
              .toLowerCase()
              .indexOf(searchText) < 0
          ) {
            return;
          }
        }
      }

      // Determine color based on mode and highlight
      let finalColorStr = ""; // mode === 'pfam' ? p.pfamColor : p.geneColor;
      if (sharedData.leftSelectedKey) {
        // match left selected key, but right panel color
        if (
          (p[`:${sharedData.leftGroupKey}Name`] as string) ===
          sharedData.leftSelectedKey
        ) {
          // finalColorStr = p[`${rightGroupKey}Color`] as string || '';
          finalColorStr = (p[`:Right:Color`] as string) || "";
        } else {
          finalColorStr = "#1e293b";
        }
      } else if (sharedData.leftGroupKey) {
        if (p[`:${sharedData.leftGroupKey}Name`] === "Unknown") {
          finalColorStr = "#1e293b";
        } else {
          finalColorStr =
            (p[`:${sharedData.leftGroupKey}Color`] as string) || "";
        }
      }

      // If highlighting is active
      colorScratch.set(finalColorStr);

      const isHighlighted =
        sharedData.rightSelectedKey &&
        (p[`:${sharedData.leftGroupKey}Name`] as string) ===
          sharedData.leftSelectedKey &&
        (p[`:${rightGroupKey}Name`] as string) === sharedData.rightSelectedKey;
      if (isHighlighted) {
        highlightedPositions.push(p.x, p.y, p.z);
        highlightedColors.push(colorScratch.r, colorScratch.g, colorScratch.b);
        highlightedPoints.push(p);
      } else {
        regularPositions.push(p.x, p.y, p.z);
        regularColors.push(colorScratch.r, colorScratch.g, colorScratch.b);
        regularPoints.push(p);
      }
    });

    return {
      regular: {
        positions: new Float32Array(regularPositions),
        colors: new Float32Array(regularColors),
        points: regularPoints,
      },
      highlighted: {
        positions: new Float32Array(highlightedPositions),
        colors: new Float32Array(highlightedColors),
        points: highlightedPoints,
      },
    };
  }, [
    data,
    sharedData.leftGroupKey,
    sharedData.leftSelectedKey,
    sharedData.rightGroupKey,
    sharedData.rightSelectedKey,
    showUnknown,
    searchText,
    searchGroup,
  ]);

  // Handle Double Click
  const handleDoubleClick = (e: any, points?: Point3D[]) => {
    e.stopPropagation();
    if (e.index !== undefined && points?.[e.index]) {
      onPointDoubleClick(points[e.index], points);
    }
  };

  const handlePointerOver = (e: any, points?: Point3D[]) => {
    e.stopPropagation();
    if (e.index !== undefined && points?.[e.index]) {
      // Pass the client coordinates for the tooltip
      onHover(points[e.index], e.clientX, e.clientY);
    }
  };

  const handlePointerOut = () => {
    onHover(null, 0, 0);
  };

  if (data.length === 0) return null;

  return (
    // Outer group rotates the view 90 degrees clockwise around the vertical axis (World Y)
    // Inner Points rotates the Data X to be Vertical (Math.PI/2 around Z)
    <group rotation={[0, -Math.PI / 2, 0]}>
      {/* Regular Points */}
      <Points
        positions={geometryData.regular.positions}
        colors={geometryData.regular.colors}
        rotation={[0, 0, Math.PI / 2]}
        frustumCulled={false}
        onDoubleClick={(e) => handleDoubleClick(e, geometryData.regular.points)}
        onPointerOver={(e) => handlePointerOver(e, geometryData.regular.points)}
        onPointerOut={handlePointerOut}
      >
        <PointMaterial
          map={circleTexture}
          alphaTest={0.5}
          transparent={false}
          vertexColors
          size={1.0}
          sizeAttenuation={true}
          depthWrite={true}
          toneMapped={false}
        />
      </Points>

      {/* Highlighted Points */}
      {geometryData.highlighted.positions.length > 0 && (
        <Points
          positions={geometryData.highlighted.positions}
          colors={geometryData.highlighted.colors}
          rotation={[0, 0, Math.PI / 2]}
          frustumCulled={false}
          onDoubleClick={(e) =>
            handleDoubleClick(e, geometryData.highlighted.points)
          }
          onPointerOver={(e) =>
            handlePointerOver(e, geometryData.highlighted.points)
          }
          onPointerOut={handlePointerOut}
        >
          <PointMaterial
            ref={highlightedMaterialRef}
            map={circleTexture}
            alphaTest={0.5}
            transparent={false}
            vertexColors
            size={2.0} // Initial size
            sizeAttenuation={true}
            depthWrite={true}
            toneMapped={false}
          />
        </Points>
      )}
    </group>
  );
};

const CloudCanvas: React.FC<CloudCanvasProps> = ({
  data,
  paused,
  onPointDoubleClick,
  onHover,
  showUnknown,
  searchText,
  searchGroup,
}) => {
  return (
    <Canvas
      className="w-full h-full block touch-none"
      gl={{ toneMapping: THREE.NoToneMapping }}
    >
      <PerspectiveCamera makeDefault position={[0, 0, 150]} fov={60} />
      <color attach="background" args={["#0f172a"]} />
      <ambientLight intensity={1} />

      <PointCloud
        data={data}
        onPointDoubleClick={onPointDoubleClick}
        onHover={onHover}
        showUnknown={showUnknown}
        searchText={searchText}
        searchGroup={searchGroup}
      />

      <OrbitControls
        makeDefault // IMPORTANT: Fixes GizmoHelper "right-hand side of 'in'..." error
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        autoRotate={!paused}
        autoRotateSpeed={0.5}
        zoomSpeed={0.8}
        rotateSpeed={0.6}
      />

      <GizmoHelper alignment="bottom-left" margin={[80, 80]}>
        <GizmoViewport
          axisColors={["#ff3333", "#33ff33", "#3333ff"]}
          labelColor="white"
        />
      </GizmoHelper>

      {/* colorCenterLine="#334155" colorGrid="#1e293b" */}
      <gridHelper args={[200, 20]} position={[0, -60, 0]} />
    </Canvas>
  );
};

export default CloudCanvas;
